export function slugify(str) {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function generateSlug(name) {
  const base = slugify(name || "client");
  const randomSuffix = Math.floor(1000 + Math.random() * 9000);
  return `${base}-${randomSuffix}`;
}

/**
 * Nombre entier de messages (questions) correspondant à un % du quota client mensuel.
 * Aligné sur le backend : max(1, floor(total × pourcent / 100)).
 */
export function messagesFromQuotaPercent(clientMonthlyMax, percent) {
  const g = Number(clientMonthlyMax);
  const p = Number(percent);
  if (!Number.isFinite(g) || g < 1 || !Number.isFinite(p) || p < 1 || p > 100) {
    return null;
  }
  return Math.max(1, Math.floor((g * p) / 100));
}

/** Date/heure de renouvellement quota (UTC), libellé français. */
export function formatQuotaResetDateFrUtc(isoString) {
  try {
    return new Date(isoString).toLocaleString("fr-FR", {
      dateStyle: "full",
      timeStyle: "short",
      timeZone: "UTC",
    });
  } catch {
    return isoString;
  }
}

/** Compte à rebours lisible jusqu'à l'ISO (ex. renouvellement mensuel). */
export function formatQuotaBannerCountdown(isoString, nowMs = Date.now()) {
  try {
    const t = new Date(isoString).getTime();
    const ms = Math.max(0, t - nowMs);
    const sec = Math.floor(ms / 1000);
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (ms <= 0) return "renouvellement en cours";
    if (d > 0) return `dans ${d} jour${d > 1 ? "s" : ""} et ${h} h`;
    if (h > 0) return `dans ${h} h ${m} min`;
    return `dans ${m} min`;
  } catch {
    return "";
  }
}

function detailObjectToMessage(detail) {
  if (typeof detail !== "object" || detail === null || Array.isArray(detail)) {
    return null;
  }
  const msg = detail.message || detail.msg;
  if (typeof msg !== "string") return null;
  if (detail.resets_at) {
    return `${msg} Renouvellement du quota le ${formatQuotaResetDateFrUtc(detail.resets_at)} (${formatQuotaBannerCountdown(detail.resets_at)}).`;
  }
  return msg;
}

/** Détail d'erreur FastAPI (string, objet quota, ou liste de validations Pydantic). */
export function formatApiErrorDetail(err, fallback = "Erreur") {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg ?? String(e)).join(", ");
  }
  if (typeof detail === "string") return detail;
  const fromObj = detailObjectToMessage(detail);
  if (fromObj) return fromObj;
  return fallback;
}

/** Message d'erreur upload avec code HTTP si disponible. */
export function formatUploadError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "string") {
    return status ? `Erreur ${status} : ${detail}` : detail;
  }
  if (status) return `Erreur serveur ${status}`;
  if (err?.code === "ECONNABORTED") return "Délai d'envoi dépassé. Réessayez avec moins de fichiers.";
  if (err?.message) return `Erreur réseau : ${err.message}`;
  return "Erreur upload HTTP";
}

/** Erreur JSON après `fetch` sur le chat stream (corps déjà parsé ou null). */
export function formatStreamChatErrorMessage(status, bodyJson) {
  const detail = bodyJson?.detail;
  if (typeof detail === "string") return detail;
  const fromObj = detailObjectToMessage(detail);
  if (fromObj) return fromObj;
  return `Erreur serveur (${status}).`;
}

export function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} Mo`;
  if (bytes < 1024 ** 4) return `${(bytes / 1024 ** 3).toFixed(1)} Go`;
  return `${(bytes / 1024 ** 4).toFixed(1)} To`;
}

const SOURCE_PCT_MIN = 72;
const SOURCE_PCT_MAX = 96;

/** Score unique (tous égaux) : courbe douce — petits scores type RRF, similarités cosinus plus hautes. */
function sourceDisplayPercentSingle(raw) {
  if (raw >= 0.45) {
    return Math.round(
      88 + Math.min(1, (raw - 0.45) / 0.55) * (SOURCE_PCT_MAX - 88)
    );
  }
  if (raw >= 0.12) {
    return Math.round(78 + ((raw - 0.12) / 0.33) * 10);
  }
  return Math.round(
    SOURCE_PCT_MIN + Math.min(1, raw / 0.08) * (SOURCE_PCT_MAX - SOURCE_PCT_MIN)
  );
}

/**
 * Pourcentages de pertinence affichés pour les sources d’une réponse.
 * Les scores bruts (RRF, cosinus, etc.) ne sont pas des « parts » ; on conserve l’ordre
 * et on projette sur une échelle lisible (évite des badges à 2 % tout en restant honnête).
 *
 * @param {Array<{ score?: number } | string>} sources
 * @returns {(number | null)[]} un pourcentage par entrée, ou null si pas de score
 */
export function sourceRelevanceDisplayPercents(sources) {
  if (!Array.isArray(sources) || sources.length === 0) return [];

  const scores = sources.map((s) => {
    if (typeof s === "string" || s == null) return null;
    const sc = s.score;
    if (typeof sc !== "number" || Number.isNaN(sc)) return null;
    return sc;
  });

  const defined = scores.filter((x) => x != null);
  if (defined.length === 0) {
    return sources.map(() => null);
  }

  const minV = Math.min(...defined);
  const maxV = Math.max(...defined);

  if (maxV > minV) {
    return sources.map((_, i) => {
      const sc = scores[i];
      if (sc == null) return null;
      const t = (sc - minV) / (maxV - minV);
      return Math.round(SOURCE_PCT_MIN + t * (SOURCE_PCT_MAX - SOURCE_PCT_MIN));
    });
  }

  const base = sourceDisplayPercentSingle(minV);
  return sources.map((_, i) => (scores[i] == null ? null : base));
}
